/**
 * Floating preview chip for the Design board (Phase 7).
 *
 * A small cursor-following label that appears 200 ms into a hover-preview
 * (synchronized with `wirePreviewOnHover`'s timer) so the user knows what
 * they're trying on by name — e.g. "Apply 'Aurora' palette".
 *
 * Lifecycle is owned by wirePreviewOnHover:
 *   - showChip(text) when the preview applies (post-delay).
 *   - hideChip() when the preview reverts or commits.
 *
 * Distinct from the global tooltip system (tooltip.js): tooltips are a
 * singleton with a fixed [data-tooltip] lookup, 300 ms delay, and dismiss
 * on click — none of which match the chip's contract.
 *
 * Implementation notes:
 *   - Single shared chip element appended to document.body lazily.
 *   - mousemove listener attached only while a chip is visible.
 *   - Position-fixed, pointer-events: none — never blocks interaction.
 *   - Reduced-motion users get the chip without the fade animation
 *     (.pb-design-board-floating-chip CSS handles the gating).
 */

let _chipEl = null;
let _mousemoveBound = false;
let _lastX = 0;
let _lastY = 0;

const CHIP_OFFSET_X = 14; // gap between cursor and chip's left edge
const CHIP_OFFSET_Y = 14; // gap between cursor and chip's top edge

function _ensureChip() {
  if (_chipEl) return _chipEl;
  // Reuse the slot defined in the design-board shell when available; fall
  // back to a body-level mount so the chip works even if the panel hasn't
  // rendered yet (defensive — should never happen in practice).
  let el = document.querySelector('.pb-design-board-floating-chip');
  if (!el) {
    el = document.createElement('div');
    el.className = 'pb-design-board-floating-chip';
    el.hidden = true;
    document.body.appendChild(el);
  }
  // The shell-mounted chip lives inside the panel (which clips overflow), so
  // re-parent to body the first time we use it. Avoids the chip being clipped
  // when the cursor moves past the panel-left edge into the canvas.
  if (el.parentNode !== document.body) {
    document.body.appendChild(el);
  }
  _chipEl = el;
  return el;
}

function _onMouseMove(e) {
  _lastX = e.clientX;
  _lastY = e.clientY;
  if (!_chipEl || _chipEl.hidden) return;
  _positionChip();
}

function _positionChip() {
  if (!_chipEl) return;
  // Default: chip's top-left sits at (cursor + offset). If that would
  // overflow the viewport, flip to the cursor's left/top side.
  const rect = _chipEl.getBoundingClientRect();
  let left = _lastX + CHIP_OFFSET_X;
  let top = _lastY + CHIP_OFFSET_Y;
  if (left + rect.width > window.innerWidth - 8) {
    left = Math.max(8, _lastX - rect.width - CHIP_OFFSET_X);
  }
  if (top + rect.height > window.innerHeight - 8) {
    top = Math.max(8, _lastY - rect.height - CHIP_OFFSET_Y);
  }
  _chipEl.style.left = `${left}px`;
  _chipEl.style.top = `${top}px`;
}

/**
 * Show the chip with the given text near the cursor. No-op when text is empty.
 * Repeated calls update the text + position without flicker.
 */
export function showChip(text) {
  if (!text) return;
  const el = _ensureChip();
  el.textContent = text;
  el.hidden = false;
  if (!_mousemoveBound) {
    document.addEventListener('mousemove', _onMouseMove, { passive: true });
    _mousemoveBound = true;
  }
  _positionChip();
}

/** Hide the chip and detach the mousemove listener. */
export function hideChip() {
  if (_chipEl) {
    _chipEl.hidden = true;
    _chipEl.textContent = '';
  }
  if (_mousemoveBound) {
    document.removeEventListener('mousemove', _onMouseMove);
    _mousemoveBound = false;
  }
}
